import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { AssetStatus } from '@verantos-dev/types-projects';

import { authFetch } from './authFetch';

// Cannot import from @verantos-dev/feature-analytical-variables barrel — that package
// imports back from feature-epi-chat (sqlAvDetailToEpiArtifact, AvContentCard), creating a
// cycle. This interface mirrors AnalyticalVariableDetail; keep in sync with that type.
export interface AvDetail {
  analyticalVariableId: string;
  organizationId: string | null;
  name: string;
  dataElement?: string | null;
  description: string | null;
  format: string | null;
  kind: string;
  owner: string | null;
  status?: string | null;
  // Backend-derived unified asset status (Draft / Processing / Ready / Error)
  // from ``analytical_variable_to_asset_status`` — resolved for the in-flight
  // draft too (``av_detail_resolver``). Tolerant string at the wire; the union
  // types consumers.
  assetStatus?: AssetStatus | null;
  scope?: string | null;
  projectId?: string | null;
  manifest?: Record<string, unknown> | null;
}

/**
 * ``options.includeDeleted`` (OR-6234): also resolve a soft-deleted variable.
 * Set by the verification result's index-anchor row, whose anchor may since
 * have been deleted (AC 6). The flag is part of the key so a plain read never
 * reuses an answer that was allowed to include a deleted row.
 */
export const useVariableDetailByName = (
  slug: string | null | undefined,
  projectId: string | undefined,
  options?: { includeDeleted?: boolean }
): UseQueryResult<{ data: AvDetail }> =>
  useQuery({
    queryKey: [
      'analytical-variables',
      'av-info',
      slug ?? '',
      projectId ?? '',
      options?.includeDeleted ? 'include-deleted' : '',
    ],
    queryFn: async (): Promise<{ data: AvDetail }> => {
      const qs = new URLSearchParams();
      if (projectId) qs.set('projectId', projectId);
      if (options?.includeDeleted) qs.set('includeDeleted', 'true');
      const qsStr = qs.toString() ? `?${qs}` : '';
      return authFetch<{ data: AvDetail }>(
        `/analytical-variables/name/${encodeURIComponent(slug ?? '')}${qsStr}`
      );
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

/**
 * Resolve an analytical variable's detail by its card ref (``av.<slug>``),
 * chat-aware so it resolves a **still-drafting** AV from the per-session
 * in-flight store — the ``/name/<slug>`` path can't (it has no session, so a
 * mid-draft read 404s until the AV is saved). Used by the inline AV card to
 * read the backend ``assetStatus`` across the whole drafting → verified
 * lifecycle. Mirrors ``feature-analytical-variables``'s ``useVariableDetail``
 * (can't import it — would cycle), hitting the same ``/by-ref`` endpoint.
 *
 * The query key intentionally mirrors ``avKeys.libraryDetail(ref, projectId)``
 * from ``feature-analytical-variables`` (``['analytical-variables', 'library',
 * <ref>, <projectId>]``). That keeps this query in the **same cache entry as the
 * AV detail page** (so the card and detail can't diverge) AND under the
 * ``avKeys.library()`` namespace that the AV ``RESOURCE_UPDATED`` invalidation
 * already clears on save/verify — so the card refetches and flips Draft→Ready
 * without a poll or any new invalidation wiring. ``feature-epi-chat`` replicates
 * AV key shapes by design (see ``resourceQueryKeys.ts`` header — importing
 * ``avKeys`` would close a build-graph cycle); keep this in sync with that
 * factory. ``chatId`` is a queryFn param, not part of the key (matching
 * ``useVariableDetail``).
 *
 * OR-6234: a card in a past conversation must still open for a variable that
 * has since been deleted (AC 5's chat-history exception), so this read always
 * asks the server to include deleted rows. Its key therefore gains a trailing
 * ``'chat'`` segment: the detail page's exact ``libraryDetail`` key no longer
 * matches, so a deleted variable fetched for a card can never be served to the
 * page from cache — while the ``avKeys.library()`` PREFIX still matches, so the
 * existing RESOURCE_UPDATED invalidation keeps flipping the card Draft→Ready.
 */
export const useVariableDetailByRef = (
  ref: string | null | undefined,
  projectId: string | undefined,
  chatId: string | null | undefined,
  options?: { enabled?: boolean }
): UseQueryResult<{ data: AvDetail }> =>
  useQuery({
    queryKey: ['analytical-variables', 'library', ref ?? '', projectId ?? '', 'chat'],
    queryFn: async (): Promise<{ data: AvDetail }> => {
      const qs = new URLSearchParams({ ref: ref ?? '' });
      if (chatId) qs.set('chat', chatId);
      if (projectId) qs.set('projectId', projectId);
      qs.set('includeDeleted', 'true');
      return authFetch<{ data: AvDetail }>(`/analytical-variables/by-ref?${qs.toString()}`);
    },
    enabled: options?.enabled !== false && !!ref,
  });
